import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leaveApplyApi } from '../api/leaveApplyApi';
import { appToast } from '../../../../components/AppToast';

export function useLeaveApplyEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const mode = searchParams.get('mode') || (editId ? 'edit' : 'new');
  const isView = mode === 'view';

  const employeeInfo = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: stored.name || stored.userName || 'Divyanshu Singh',
        empId: stored.employee_id || stored.empCode || '348414',
      };
    } catch {
      return {
        name: 'Divyanshu Singh',
        empId: '348414',
      };
    }
  }, []);

  const [applyDate, setApplyDate] = useState('2026-08-31');
  const [employee, setEmployee] = useState(employeeInfo.name);
  const [fromDate, setFromDate] = useState('2026-08-31');
  const [tillDate, setTillDate] = useState('2026-08-31');
  const [type, setType] = useState('FullDay');
  const [leaveType, setLeaveType] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const typeOptions = [
    { value: 'FullDay', label: 'FullDay' },
    { value: 'HalfDay', label: 'HalfDay' },
  ];

  const leaveTypeOptions = [
    { value: '', label: 'Select' },
    { value: 'Casual Leave', label: 'Casual Leave' },
    { value: 'Sick Leave', label: 'Sick Leave' },
    { value: 'Privilege Leave', label: 'Privilege Leave' },
    { value: 'Maternity / Paternity Leave', label: 'Maternity / Paternity Leave' },
    { value: 'Compensatory Off', label: 'Compensatory Off' },
    { value: 'Leave Without Pay (LWP)', label: 'Leave Without Pay (LWP)' },
  ];

  const reasonOptions = [
    { value: '', label: 'Select' },
    { value: 'Personal Work', label: 'Personal Work' },
    { value: 'Medical / Sickness', label: 'Medical / Sickness' },
    { value: 'Family Emergency', label: 'Family Emergency' },
    { value: 'Travel / Vacation', label: 'Travel / Vacation' },
    { value: 'Festival / Celebration', label: 'Festival / Celebration' },
    { value: 'Others', label: 'Others' },
  ];

  // Calculated Days
  const calculatedDays = useMemo(() => {
    if (type === 'HalfDay') return 0.5;
    if (!fromDate || !tillDate) return 1;
    const start = new Date(fromDate);
    const end = new Date(tillDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, diffDays);
  }, [fromDate, tillDate, type]);

  // Load Record if Editing/Viewing
  useEffect(() => {
    async function init() {
      try {
        const balRes = await leaveApplyApi.getBalances();
        if (balRes.data) setLeaveBalances(balRes.data);

        if (editId) {
          const res = await leaveApplyApi.getById(editId);
          if (res.data) {
            const r = res.data;
            if (r.applyDate) setApplyDate(r.applyDate);
            if (r.name) setEmployee(r.name);
            if (r.fromDate) setFromDate(r.fromDate);
            if (r.tillDate) setTillDate(r.tillDate);
            if (r.type) setType(r.type);
            if (r.leaveType) setLeaveType(r.leaveType);
            if (r.reason) setReason(r.reason);
            if (r.remarks) setRemarks(r.remarks);
          }
        }
      } catch (err) {
        console.error('Failed to load leave details', err);
      }
    }
    init();
  }, [editId]);

  const validate = () => {
    if (!fromDate) {
      appToast.error('Please select From Date');
      return false;
    }
    if (!tillDate) {
      appToast.error('Please select Till Date');
      return false;
    }
    if (!leaveType) {
      appToast.error('Please select a Leave Type');
      return false;
    }
    if (!reason) {
      appToast.error('Please select Reason for leave');
      return false;
    }
    if (!remarks.trim()) {
      appToast.error('Please enter Remarks');
      return false;
    }
    return true;
  };

  const handleSaveAndExit = async (e) => {
    if (e) e.preventDefault();
    if (isView) return;
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name: employee,
        empId: employeeInfo.empId,
        applyDate,
        fromDate,
        tillDate,
        leaveDate: fromDate === tillDate ? fromDate : `${fromDate} - ${tillDate}`,
        days: calculatedDays,
        type,
        leaveType,
        reason,
        remarks,
        status: 'Pending',
      };

      if (editId) {
        await leaveApplyApi.update(editId, payload);
        appToast.success('Leave application updated successfully');
      } else {
        await leaveApplyApi.create(payload);
        appToast.success('Leave application submitted successfully');
      }
      navigate('/leave-apply');
    } catch {
      appToast.error('Failed to submit leave application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async (e) => {
    if (e) e.preventDefault();
    if (isView) return;
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name: employee,
        empId: employeeInfo.empId,
        applyDate,
        fromDate,
        tillDate,
        leaveDate: fromDate === tillDate ? fromDate : `${fromDate} - ${tillDate}`,
        days: calculatedDays,
        type,
        leaveType,
        reason,
        remarks,
        status: 'Pending',
      };

      if (editId) {
        await leaveApplyApi.update(editId, payload);
        appToast.success('Leave application saved');
      } else {
        await leaveApplyApi.create(payload);
        appToast.success('Leave application submitted');
        setLeaveType('');
        setReason('');
        setRemarks('');
      }
    } catch {
      appToast.error('Failed to save leave application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/leave-apply');
  };

  return {
    state: {
      isEdit: Boolean(editId) && !isView,
      isView,
      mode,
      editId,
      applyDate,
      employee,
      fromDate,
      tillDate,
      days: calculatedDays,
      type,
      leaveType,
      reason,
      remarks,
      leaveBalances,
      isSaving,
      typeOptions,
      leaveTypeOptions,
      reasonOptions,
    },
    actions: {
      setFromDate,
      setTillDate,
      setType,
      setLeaveType,
      setReason,
      setRemarks,
      handleSaveAndExit,
      handleSaveAndContinue,
      handleCancel,
    },
  };
}
